import { isLabsResourceID } from "@/client/utils/isLabsResourceID";
import { isSpliceSearchResult, type SpliceSearchResult } from "@/shared/splice-search.api";
import { isObject, isStringArray } from "@/shared/utils/typeGuards";
import { isSoundsBrowserModeId, type SoundsBrowserModeId } from "./modes/sounds-browser-modes";
import type { SamplePreviewResume } from "./sample-preview-player";
import type { SoundsBrowserFilters, SoundsBrowserSeed } from "./sounds-browser.api";

/**
 * What the browser was showing when the page changed, so the next page can
 * show it again without asking Atlantis. Labs is a multi-page app: home to an
 * app is a full load, and without this the dock would come back empty every
 * time. Restoring from the results already in hand rather than re-searching
 * is also what keeps a page change from counting as another Splice Search in
 * telemetry.
 */
export interface SoundsBrowserSession {
    readonly modeId: SoundsBrowserModeId;
    readonly seed: SoundsBrowserSeed | undefined;
    readonly selectedUuid: string | undefined;
    readonly resume: SamplePreviewResume | undefined;
    readonly query: string;
    readonly filters: SoundsBrowserFilters;
    readonly results: readonly SpliceSearchResult[];
    readonly tagSummary: readonly string[];
    /** The Atlantis page the results run to, so "load more" continues from it. */
    readonly page: number;
    readonly hasMore: boolean;
    readonly scrollTop: number;
    readonly savedAt: number;
}

/**
 * Preview links may be signed, and a tab left open overnight should start
 * fresh rather than show a list whose audio has expired. Half an hour is long
 * enough to cover any hop between apps.
 */
export const kSessionMaxAge_ms = 30 * 60 * 1000;

export const kSessionStorageKey = "labs-live:sounds-browser:session";

/** The tab's own storage: a browse is a session, and a new tab starts clean. */
const getStorage = (): Storage | undefined => {
    try {
        return typeof sessionStorage === "undefined" ? undefined : sessionStorage;
    } catch {
        return undefined;
    }
};

const isFilters = (value: unknown): value is SoundsBrowserFilters =>
    isObject(value) &&
    typeof value.isLibraryOnly === "boolean" &&
    typeof value.category === "string" &&
    isStringArray(value.instruments) &&
    isStringArray(value.genres) &&
    isStringArray(value.tags) &&
    typeof value.key === "string" &&
    typeof value.chordType === "string" &&
    (value.bpmMin === undefined || typeof value.bpmMin === "number") &&
    (value.bpmMax === undefined || typeof value.bpmMax === "number") &&
    typeof value.sort === "string";

export const isSoundsBrowserSeed = (value: unknown): value is SoundsBrowserSeed =>
    isObject(value) &&
    ((value.kind === "catalog" && isSpliceSearchResult(value.result)) ||
        (value.kind === "resource" &&
            isLabsResourceID(value.id, "wav") &&
            typeof value.name === "string" &&
            (value.bpm === null || typeof value.bpm === "number") &&
            typeof value.namespace === "string"));

export const isSamplePreviewResume = (value: unknown): value is SamplePreviewResume =>
    isObject(value) &&
    typeof value.uuid === "string" &&
    typeof value.position_s === "number" &&
    Number.isFinite(value.position_s);

export const isSoundsBrowserSession = (value: unknown): value is SoundsBrowserSession =>
    isObject(value) &&
    isSoundsBrowserModeId(value.modeId) &&
    (value.seed === undefined || isSoundsBrowserSeed(value.seed)) &&
    (value.selectedUuid === undefined || typeof value.selectedUuid === "string") &&
    (value.resume === undefined || isSamplePreviewResume(value.resume)) &&
    typeof value.query === "string" &&
    isFilters(value.filters) &&
    Array.isArray(value.results) &&
    value.results.every(isSpliceSearchResult) &&
    isStringArray(value.tagSummary) &&
    typeof value.page === "number" &&
    typeof value.hasMore === "boolean" &&
    typeof value.scrollTop === "number" &&
    typeof value.savedAt === "number";

export const parseSoundsBrowserSession = (
    raw: string | null,
    now: number,
): SoundsBrowserSession | undefined => {
    if (raw === null) {
        return undefined;
    }
    try {
        const parsed: unknown = JSON.parse(raw);
        if (!isSoundsBrowserSession(parsed)) {
            return undefined;
        }
        if (now - parsed.savedAt > kSessionMaxAge_ms) {
            return undefined;
        }
        // A seed with nothing found yet is still worth carrying; an empty
        // search with no seed is not.
        if (parsed.results.length === 0 && parsed.seed === undefined) {
            return undefined;
        }
        return parsed;
    } catch {
        return undefined;
    }
};

export const loadSoundsBrowserSession = (): SoundsBrowserSession | undefined => {
    const storage = getStorage();
    if (storage === undefined) {
        return undefined;
    }
    try {
        return parseSoundsBrowserSession(storage.getItem(kSessionStorageKey), Date.now());
    } catch {
        return undefined;
    }
};

export const saveSoundsBrowserSession = (session: SoundsBrowserSession): void => {
    const storage = getStorage();
    if (storage === undefined) {
        return;
    }
    try {
        if (session.results.length === 0 && session.seed === undefined) {
            storage.removeItem(kSessionStorageKey);
            return;
        }
        // `undefined` fields drop out of JSON, which the validator allows.
        storage.setItem(kSessionStorageKey, JSON.stringify(session));
    } catch {
        // Quota or a blocked store: the browse simply does not carry over.
    }
};

export const clearSoundsBrowserSession = (): void => {
    const storage = getStorage();
    try {
        storage?.removeItem(kSessionStorageKey);
    } catch {
        // Nothing to clear.
    }
};
